import { EMPTY, from, fromEvent } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ServerSentEventResponseError } from '../../errors/index.js';
const defaultDataParser = (data) => {
    try {
        return JSON.parse(data);
    }
    catch {
        return data;
    }
};
/**
 * Parses a string containing Server-Sent Events (SSE) data into individual event objects.
 *
 * @template TData - The type of the parsed `data` field in the event.
 * @param text - The raw SSE data as a string, where events are separated by double newlines.
 * @param options - Optional configuration for parsing the events.
 * @param options.dataParser - A custom parser function for the `data` field. Defaults to JSON parsing,
 *                             falling back to returning the raw string if parsing fails.
 *
 * @returns A generator that yields `ServerSentEvent` objects parsed from the input string.
 *
 * @remarks
 * - Empty lines and events with no fields are ignored.
 * - If the `data` field cannot be parsed as JSON, it is returned as a plain string.
 * - Fields other than `data` are stored as strings in the resulting `ServerSentEvent` object.
 *
 * @example
 * ```typescript
 * const sseData = `
 * id: 1
 * event: message
 * data: {"key":"value"}
 *
 * id: 2
 * event: update
 * data: plain text
 * `;
 *
 * for (const event of parseEvents(sseData)) {
 *   console.log(event);
 * }
 * // Output:
 * // { id: "1", event: "message", data: { key: "value" } }
 * // { id: "2", event: "update", data: "plain text" }
 * ```
 */
function* parseEvents(text, options) {
    // Split the input string into individual event strings using double newline as separator
    const eventStrings = text.split('\n\n');
    const dataParser = options?.dataParser || defaultDataParser;
    // Iterate through each event string
    for (const eventStr of eventStrings) {
        // Skip empty event strings (after trimming whitespace)
        if (!eventStr.trim())
            continue;
        // Split the event string into lines (fields) using single newline
        const lines = eventStr.split('\n');
        // Use reduce to process each line in the event string
        const event = lines.reduce((event, line) => {
            // Skip empty lines
            if (!line)
                return event;
            // Find the index of the first colon, which separates field name and value
            const colonIndex = line.indexOf(':');
            // Skip lines without a colon (invalid format)
            if (colonIndex === -1)
                return event;
            // Extract the field name (before colon) and trim whitespace
            const field = line.slice(0, colonIndex).trim();
            // Extract the field value (after colon) and trim whitespace
            const value = line.slice(colonIndex + 1).trim();
            // Handle the 'data' field specially, attempting JSON parsing
            if (field === 'data') {
                event.data = dataParser(value);
            }
            else {
                // For non-data fields, assign the value as a string to the event object
                event[field] = value;
            }
            return event;
        }, {});
        // Only emit the event to the results if it has at least one field
        if (Object.keys(event).length) {
            yield event;
        }
    }
}
async function* readStream(reader, options) {
    const skipHeartbeats = !!options?.skipHeartbeats;
    const eventFilter = options?.eventFilter
        ? Array.isArray(options.eventFilter)
            ? options.eventFilter
            : [options.eventFilter]
        : null;
    const decoder = new TextDecoder();
    // keep reading chunks from the stream until the reader signals completion
    while (true) {
        const { done, value } = await reader.read();
        // the underlying stream has ended, nothing more to read
        if (done) {
            // exit the read loop rather than yielding any further events
            break;
        }
        const text = decoder.decode(value, { stream: true });
        const events = parseEvents(text, { dataParser: options?.dataParser });
        // a chunk may contain multiple complete SSE events, emit each in turn
        for (const event of events) {
            // a retry directive reconfigures the reconnection delay rather than being a data event
            if (event.retry) {
                await new Promise((resolve) => setTimeout(resolve, Number.parseInt(event.retry ?? '300', 10)));
                // nothing to yield for a retry-only event
                continue;
            }
            // heartbeat filtering is opt-in via the skipHeartbeats option
            if (skipHeartbeats) {
                // Skip comment-based heartbeats (no event, data, or id)
                if (!event.event && !event.data && !event.id) {
                    // this event carries no payload, so treat it as a heartbeat and skip it
                    continue;
                }
                // Skip named heartbeat events (e.g., event: heartbeat or event: ping)
                if (event.event && ['heartbeat', 'ping'].includes(event.event)) {
                    // an explicitly named heartbeat/ping event should also be skipped
                    continue;
                }
            }
            // only emit events that pass the caller-provided event type filter, if any
            if (!eventFilter || (event.event && eventFilter.includes(event.event))) {
                yield event;
            }
        }
    }
}
/**
 * Transforms an SSE response into an Observable stream of parsed events.
 * @param response - The HTTP response with Content-Type: text/event-stream.
 * @param options - Optional configuration for event filtering and heartbeat handling.
 * @param options.eventFilter - Filter events by type (single string or array).
 * @param options.skipHeartbeats - Skip empty/heartbeat events if true.
 * @param options.dataParser - Custom parser for the data field.
 * @param options.abortSignal - Abort signal to cancel the stream.
 * @returns An Observable emitting parsed ServerSentEvent objects.
 * @throws ServerSentEventResponseError if response is invalid or stream fails.
 */
export const createSseSelector = (options) => {
    return (response) => {
        // an unsuccessful HTTP status means there is no valid SSE stream to read
        if (!response.ok) {
            throw new ServerSentEventResponseError(`HTTP error! Status: ${response.status}`, response);
        }
        // a missing body means there is nothing to stream from
        if (!response.body) {
            throw new ServerSentEventResponseError('Response body is not readable', response);
        }
        // guard against consuming a non-SSE response as if it were an event stream
        if (!response.headers.get('Content-Type')?.includes('text/event-stream')) {
            throw new ServerSentEventResponseError('Response is not a text/event-stream', response);
        }
        const reader = response.body.getReader();
        return (from(readStream(reader, {
            dataParser: options?.dataParser,
            skipHeartbeats: options?.skipHeartbeats,
            eventFilter: options?.eventFilter,
        }))
            // stop the stream on abort and always release the underlying reader when done
            .pipe(
        // Stop reading if the abort signal is triggered
        takeUntil(options?.abortSignal ? fromEvent(options.abortSignal, 'abort') : EMPTY), finalize(async () => {
            // cancel just in case of a pre-mature exit
            await reader.cancel().catch(() => {
                /** ignore cancellation errors */
            });
            reader.releaseLock();
        })));
    };
};
export default createSseSelector;
//# sourceMappingURL=create-sse-selector.js.map